import { useState, useRef, useCallback } from 'react';

// ---- Types ----

interface Deal {
  name: string;
  lat: number;
  lng: number;
  units: string;
  basis: string;
  irr: string;
  sc: string;
}

interface ScifiMapProps {
  deals: Deal[];
  hoverDeal?: number;
  onDotClick?: (index: number) => void;
  onDotHover?: (index: number) => void;
  onDotLeave?: () => void;
}

// ---- Constants ----

const VW = 560;
const VH = 340;
const GRID_SPACING = 36;

// Extracted from USGS cartographic projection via OpenCV contour detection
const US_PATH =
  'M26.0,13.4L26.0,20.5L32.0,37.2L32.0,65.6L30.0,68.0L30.0,79.9L28.0,82.2L28.0,98.9L30.0,101.2L30.0,115.4L28.0,117.8L28.0,122.6L34.0,132.0L34.0,141.6L39.9,148.7L39.9,153.5L45.9,158.1L45.9,165.2L51.8,170.0L49.8,174.8L59.7,189.0L61.7,198.5L63.7,200.8L71.6,200.8L73.6,205.6L77.6,205.6L83.5,210.4L91.5,219.8L91.5,224.6L93.5,226.9L111.4,224.6L115.3,229.4L119.3,229.4L145.1,243.6L170.9,243.6L174.8,236.5L186.7,236.5L188.7,241.2L190.7,241.2L194.7,248.3L202.6,255.4L202.6,262.6L210.5,272.1L218.5,274.4L220.5,267.3L224.5,262.6L226.5,265.0L232.4,265.0L238.4,272.1L244.3,288.6L250.3,298.2L252.2,307.6L262.2,314.8L270.1,314.8L268.1,310.0L268.1,291.1L276.0,281.5L287.9,274.4L287.9,272.1L295.9,265.0L301.8,265.0L303.8,262.6L311.7,267.3L317.7,265.0L321.6,267.3L323.6,272.1L333.5,272.1L335.5,269.8L341.5,274.4L343.5,272.1L337.5,267.3L341.5,262.6L341.5,257.8L343.5,255.4L349.5,255.4L351.5,257.8L355.4,257.8L357.4,255.4L367.3,255.4L371.3,257.8L375.3,265.0L379.2,265.0L387.2,260.2L395.1,269.8L395.1,272.1L399.1,274.4L399.1,281.5L397.1,284.0L399.1,298.2L405.0,307.6L407.0,314.8L412.9,321.9L412.9,326.7L418.9,324.3L420.9,312.4L422.8,310.0L422.8,300.5L418.9,293.4L416.9,279.2L410.9,262.6L410.9,253.1L409.0,250.7L410.9,248.3L412.9,236.5L416.9,229.4L428.8,219.8L436.7,208.1L440.7,208.1L444.7,200.8L452.6,198.5L454.6,196.2L454.6,191.4L462.5,184.3L458.5,165.2L466.5,151.0L468.5,139.1L476.5,129.7L476.5,122.6L480.4,117.8L492.3,115.4L494.3,113.1L492.3,110.7L494.3,108.3L500.3,108.3L502.2,106.0L512.2,110.7L512.2,101.2L504.2,96.4L506.2,91.8L504.2,87.0L510.2,77.4L518.1,75.1L522.1,68.0L524.1,70.3L537.9,63.2L537.9,58.5L532.0,49.0L532.0,32.4L528.0,27.6L524.1,27.6L522.1,30.1L518.1,27.6L512.2,34.7L508.2,44.3L508.2,49.0L502.2,56.1L498.3,56.1L496.3,58.5L468.5,58.5L454.6,70.3L454.6,75.1L456.6,77.4L450.6,82.2L430.8,82.2L430.8,87.0L432.8,89.3L428.8,94.1L416.9,101.2L412.9,101.2L409.0,106.0L403.0,106.0L401.0,108.3L393.1,103.5L395.1,96.4L401.0,91.8L401.0,79.9L399.1,77.4L399.1,72.8L393.1,72.8L391.1,70.3L393.1,68.0L393.1,56.1L381.2,49.0L383.2,46.6L387.2,46.6L385.2,39.5L381.2,39.5L379.2,37.2L359.4,39.5L355.4,34.7L351.5,34.7L349.5,32.4L353.4,27.6L351.5,25.3L343.5,32.4L335.5,34.7L329.6,39.5L327.6,34.7L323.6,34.7L321.6,32.4L329.6,22.8L333.5,22.8L337.5,20.5L337.5,18.2L327.6,18.2L325.6,15.7L323.6,18.2L319.7,18.2L309.7,11.1L303.8,11.1L301.8,13.4L299.8,11.1L293.9,11.1L291.9,8.6L291.9,1.5L287.9,1.5L285.9,6.3L41.9,6.3L39.9,13.4L43.9,15.7L41.9,18.2L34.0,18.2Z';

// ---- Helpers ----

export function toMapXY(lat: number, lng: number): [number, number] {
  const x = 30 + ((lng - -125) / (-67 - -125)) * 500;
  const y = 6 + ((49.5 - lat) / (49.5 - 24.5)) * 342;
  return [x, y];
}

type TextAnchor = 'start' | 'middle' | 'end';

interface LabelOffset {
  dx: number;
  dy: number;
  anchor: TextAnchor;
}

interface PlacedRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function computeLabelOffsets(
  deals: Deal[],
): LabelOffset[] {
  const labelH = 10;
  const pad = 4;
  const placed: PlacedRect[] = [];

  const positions = deals.map((d) => ({
    pos: toMapXY(d.lat, d.lng),
    name: d.name.split(' ')[0],
  }));

  return positions.map(({ pos: [cx, cy], name }) => {
    const candidates: LabelOffset[] = [
      { dx: 0, dy: -12, anchor: 'middle' },
      { dx: 0, dy: 16, anchor: 'middle' },
      { dx: 12, dy: 3, anchor: 'start' },
      { dx: -12, dy: 3, anchor: 'end' },
      { dx: 10, dy: -10, anchor: 'start' },
      { dx: -10, dy: -10, anchor: 'end' },
      { dx: 10, dy: 14, anchor: 'start' },
      { dx: -10, dy: 14, anchor: 'end' },
    ];
    const estW = name.length * 4.5;
    for (const c of candidates) {
      const lx =
        c.anchor === 'middle'
          ? cx + c.dx - estW / 2
          : c.anchor === 'start'
            ? cx + c.dx
            : cx + c.dx - estW;
      const ly = cy + c.dy - labelH / 2;
      const rect: PlacedRect = {
        x: lx - pad,
        y: ly - pad,
        w: estW + pad * 2,
        h: labelH + pad * 2,
      };
      const overlaps = placed.some(
        (p) =>
          rect.x < p.x + p.w &&
          rect.x + rect.w > p.x &&
          rect.y < p.y + p.h &&
          rect.y + rect.h > p.y,
      );
      if (
        !overlaps &&
        lx > 5 &&
        lx + estW < VW - 5 &&
        ly > 5 &&
        ly + labelH < VH - 5
      ) {
        placed.push(rect);
        return c;
      }
    }
    // fallback: above
    const fb = candidates[0];
    placed.push({
      x: cx - estW / 2 - pad,
      y: cy + fb.dy - labelH / 2 - pad,
      w: estW + pad * 2,
      h: labelH + pad * 2,
    });
    return fb;
  });
}

// ---- Component ----

export function ScifiMap({
  deals,
  hoverDeal = -1,
  onDotClick,
  onDotHover,
  onDotLeave,
}: ScifiMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [mouse, setMouse] = useState<{ x: number; y: number } | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;
      try {
        const ctm = svg.getScreenCTM();
        if (ctm) {
          const x = (e.clientX - ctm.e) / ctm.a;
          const y = (e.clientY - ctm.f) / ctm.d;
          setMouse({ x, y });
          return;
        }
      } catch (_) {
        /* fallback below */
      }
      const rect = svg.getBoundingClientRect();
      setMouse({
        x: ((e.clientX - rect.left) / rect.width) * VW,
        y: ((e.clientY - rect.top) / rect.height) * VH,
      });
    },
    [],
  );

  const handleMouseLeave = useCallback(() => setMouse(null), []);

  const labelOffsets = computeLabelOffsets(deals);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'var(--color-void)',
        backgroundImage: `
          linear-gradient(to right, var(--color-accent-glow) 1px, transparent 1px),
          linear-gradient(to bottom, var(--color-accent-glow) 1px, transparent 1px)
        `,
        backgroundSize: `${GRID_SPACING}px ${GRID_SPACING}px`,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`0 0 ${VW} ${VH}`}
        preserveAspectRatio="xMidYMid meet"
        overflow="visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: 'crosshair' }}
      >
        {/* Ambient cursor crosshairs */}
        {mouse && (
          <g>
            <line
              x1={mouse.x}
              y1={-500}
              x2={mouse.x}
              y2={VH + 500}
              stroke="var(--color-accent)"
              strokeWidth="0.7"
              opacity={hoverDeal >= 0 ? '0.12' : '0.3'}
              strokeDasharray="2,3"
            />
            <line
              x1={-500}
              y1={mouse.y}
              x2={VW + 500}
              y2={mouse.y}
              stroke="var(--color-accent)"
              strokeWidth="0.7"
              opacity={hoverDeal >= 0 ? '0.12' : '0.3'}
              strokeDasharray="2,3"
            />
            <circle
              cx={mouse.x}
              cy={mouse.y}
              r="2"
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="0.5"
              opacity={hoverDeal >= 0 ? '0.12' : '0.35'}
            />
          </g>
        )}
        <g transform="translate(0, 10)">
          <path
            d={US_PATH}
            fill="#00c8530a"
            stroke="var(--color-accent)"
            strokeWidth="1.2"
            opacity="0.5"
            style={{ filter: 'drop-shadow(0 0 8px #00c85322)' }}
          />
          {deals.map((d, i) => {
            const [cx, cy] = toMapXY(d.lat, d.lng);
            const isHovered = hoverDeal === i;
            const lo = labelOffsets[i];
            // Hover tooltip: flip below if too close to top
            const hoverAbove = cy > 55;
            const stemEnd = hoverAbove ? cy - 24 : cy + 24;
            const boxY = hoverAbove ? cy - 48 : cy + 26;
            const nameY = boxY + 13;
            const statsY = hoverAbove ? cy + 20 : cy - 14;
            return (
              <g key={i}>
                {isHovered && (
                  <>
                    <circle
                      cx={cx}
                      cy={cy}
                      r="18"
                      fill="none"
                      stroke={d.sc}
                      strokeWidth="0.6"
                      opacity="0.3"
                    >
                      <animate
                        attributeName="r"
                        from="8"
                        to="28"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        from="0.4"
                        to="0"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </circle>
                    <circle
                      cx={cx}
                      cy={cy}
                      r="12"
                      fill="none"
                      stroke={d.sc}
                      strokeWidth="0.5"
                      opacity="0.2"
                    >
                      <animate
                        attributeName="r"
                        from="6"
                        to="20"
                        dur="2s"
                        begin="0.6s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        from="0.3"
                        to="0"
                        dur="2s"
                        begin="0.6s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </>
                )}
                <circle
                  cx={cx}
                  cy={cy}
                  r={isHovered ? 14 : 7}
                  fill={d.sc}
                  opacity={isHovered ? 0.15 : 0.08}
                  style={{
                    transition: 'all 0.5s cubic-bezier(0.2,0,0,1)',
                  }}
                />
                <circle
                  cx={cx}
                  cy={cy}
                  r={isHovered ? 5.5 : 3.5}
                  fill={d.sc}
                  opacity={isHovered ? 1 : 0.7}
                  onClick={() => onDotClick?.(i)}
                  onMouseEnter={() => onDotHover?.(i)}
                  onMouseLeave={() => onDotLeave?.()}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.2,0,0,1)',
                    filter: isHovered
                      ? `drop-shadow(0 0 6px ${d.sc}) drop-shadow(0 0 14px ${d.sc}88)`
                      : `drop-shadow(0 0 3px ${d.sc}66)`,
                  }}
                />
                {!isHovered && (
                  <text
                    x={cx + lo.dx}
                    y={cy + lo.dy}
                    textAnchor={lo.anchor}
                    style={{
                      fontSize: '7px',
                      fontFamily: 'JetBrains Mono, monospace',
                      fill: d.sc,
                      fontWeight: 500,
                      opacity: 0.6,
                    }}
                  >
                    {d.name.split(' ')[0]}
                  </text>
                )}
                {isHovered && (
                  <>
                    <line
                      x1={cx}
                      y1={-500}
                      x2={cx}
                      y2={VH + 500}
                      stroke={d.sc}
                      strokeWidth="0.9"
                      opacity="0.5"
                      strokeDasharray="3,3"
                    />
                    <line
                      x1={-500}
                      y1={cy}
                      x2={VW + 500}
                      y2={cy}
                      stroke={d.sc}
                      strokeWidth="0.9"
                      opacity="0.5"
                      strokeDasharray="3,3"
                    />
                    <line
                      x1={cx}
                      y1={hoverAbove ? cy - 9 : cy + 9}
                      x2={cx}
                      y2={stemEnd}
                      stroke={d.sc}
                      strokeWidth="0.6"
                      opacity="0.5"
                    />
                    <rect
                      x={Math.max(5, Math.min(cx - 68, VW - 141))}
                      y={boxY}
                      width="136"
                      height="18"
                      rx="2"
                      fill="var(--color-void)"
                      fillOpacity="0.92"
                      stroke={d.sc}
                      strokeWidth="0.5"
                      opacity="0.85"
                    />
                    <text
                      x={Math.max(73, Math.min(cx, VW - 73))}
                      y={nameY}
                      textAnchor="middle"
                      style={{
                        fontSize: '11px',
                        fontFamily: 'JetBrains Mono, monospace',
                        fill: d.sc,
                        fontWeight: 600,
                        letterSpacing: '0.3px',
                      }}
                    >
                      {d.name.length > 20
                        ? d.name.slice(0, 20) + '\u2026'
                        : d.name}
                    </text>
                    <text
                      x={cx}
                      y={statsY}
                      textAnchor="middle"
                      style={{
                        fontSize: '10px',
                        fontFamily: 'JetBrains Mono, monospace',
                        fill: 'var(--color-text-muted)',
                        fontWeight: 500,
                      }}
                    >
                      {d.units} units &middot; {d.basis} &middot; {d.irr} IRR
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      {/* Coordinate readout overlay */}
      {mouse && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 12,
            fontSize: '8px',
            fontFamily: 'JetBrains Mono, monospace',
            color: 'var(--color-text-dim)',
            pointerEvents: 'none',
            letterSpacing: '0.5px',
          }}
        >
          {mouse.x.toFixed(0)}, {mouse.y.toFixed(0)}
        </div>
      )}
    </div>
  );
}
